import { useContext, useEffect, useMemo, useState } from 'react'

import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import type { CRMObject, DayObjectSearchEntry } from 'types/graphql'

import { DayContext } from 'src/lib/dayContext'
import { NativeObjectTypes } from 'src/lib/objects'

import { getDayObjectsByType } from '../Objects/searchIndex'
import Row from '../Row/Row'

const ContactSelect = ({
  onSelect,
  exclude = [],
  editing = false,
  size = 'medium',
  disabled,
  sx = {},
  value = [],
  onKeyDown,
}: {
  onSelect: (contact: CRMObject[]) => void
  exclude?: string[]
  size?: 'small' | 'medium'
  disabled?: boolean
  sx?: any
  value?: DayObjectSearchEntry[]
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  editing?: boolean
}) => {
  const { selectedWorkspace } = useContext(DayContext)
  const [contacts, setContacts] = useState([])

  useEffect(() => {
    const fetchContacts = async () => {
      if (selectedWorkspace) {
        const fetchedContacts = await getDayObjectsByType({
          workspaceId: selectedWorkspace,
          objectType: NativeObjectTypes.Contact,
        })
        setContacts(fetchedContacts)
      }
    }
    fetchContacts()
  }, [selectedWorkspace])

  const options = useMemo(
    () =>
      (contacts as DayObjectSearchEntry[])?.filter(
        (person) => !exclude.includes(person.objectId) && person.objectId
      ),
    [contacts, exclude]
  )

  return (
    <Autocomplete
      sx={{
        ...sx,
        flexGrow: 1,
        '& .MuiChip-root': {
          borderRadius: '6px',
          height: '26px',
          py: 0,
          my: 0,
          '& .MuiAvatar-root': {
            ml: '6px',
          },
          '& .MuiChip-deleteIcon': {
            fontSize: '18px',
            mr: '6px',
          },
          '& .MuiChip-label': {
            fontSize: '0.7rem',
            fontWeight: 600,
            my: '4px',
            mr: '4px',
          },
        },
      }}
      options={options || []}
      getOptionLabel={(option) => option.label}
      onChange={(e, v) =>
        onSelect(
          v.map((v) => ({
            objectId: v.objectId,
            objectType: v.objectType,
            properties: v.object.properties,
          })) as CRMObject[]
        )
      }
      multiple={true}
      isOptionEqualToValue={(option, value) =>
        option.objectId === value.objectId
      }
      disabled={disabled}
      value={value}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{
            '& .MuiInputBase-root': {
              borderBottom: 'none',
              p: 0,
            },
          }}
          size={size}
          variant="standard"
          autoFocus={editing}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            endAdornment: null,
          }}
          onKeyDown={onKeyDown}
        />
      )}
      renderOption={(props, option) => {
        return (
          <Row
            {...props}
            key={option.objectId}
            sx={{
              fontSize: '0.7rem',
              px: 1,
              position: 'relative',
              zIndex: 9999,
            }}
          >
            <Box sx={{ justifyContent: 'space-between', flexGrow: 1 }}>
              <Box
                sx={{
                  ml: 0,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                {option.label}
              </Box>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: '0.7rem',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                {option.objectId}
              </Typography>
            </Box>
          </Row>
        )
      }}
    />
  )
}

export default ContactSelect
